import type { IconDefinition } from '@awesome.me/kit-a1e2571845/icons'
import { faUser, faUserGear, faUserKey, faUserLock } from '@awesome.me/kit-a1e2571845/icons/classic/regular'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import { Icon } from '@xweather/theme'
import type { ComponentProps } from 'react'
import type { UserRoleEntity } from '../../../api/user-manager.ts'
import { I18n } from '../../../components/I18n.tsx'
import type { InsightUser, Roles } from '../../../data/users.ts'

export const canEditRole = (currentUser: InsightUser, userRole: UserRoleEntity) => {
	if (currentUser.role.name === 'SUPER_ADMIN') return true
	if (currentUser.role.name === 'ADMIN') return ['ADMIN', 'LIMITED_ADMIN', 'USER'].includes(userRole.name)
	if (currentUser.role.name === 'LIMITED_ADMIN') return ['LIMITED_ADMIN', 'USER'].includes(userRole.name)
	return false
}

export const getSelectableRoles = (currentUser: InsightUser, roles: Roles) =>
	([] as UserRoleEntity[])
		.concat(roles.user && canEditRole(currentUser, roles.user) ? [roles.user] : [])
		.concat(roles.limitedAdmin && canEditRole(currentUser, roles.limitedAdmin) ? [roles.limitedAdmin] : [])
		.concat(roles.admin && canEditRole(currentUser, roles.admin) ? [roles.admin] : [])
		.concat(roles.internalReadOnly && canEditRole(currentUser, roles.internalReadOnly) ? [roles.internalReadOnly] : [])
		.concat(roles.superAdmin && canEditRole(currentUser, roles.superAdmin) ? [roles.superAdmin] : [])

export const RoleDescriptions = () => (
	<Typography component='ul' variant='body2' color='textSecondary' sx={{ m: 0, pl: 3 }}>
		<li>
			<I18n i18nKey={($) => $.users.roleDescriptions.USER} />
		</li>
		<li>
			<I18n i18nKey={($) => $.users.roleDescriptions.LIMITED_ADMIN} />
		</li>
		<li>
			<I18n i18nKey={($) => $.users.roleDescriptions.ADMIN} />
		</li>
	</Typography>
)

export const RoleIcon = ({ role, ...props }: { role: UserRoleEntity } & Omit<ComponentProps<typeof Icon>, 'icon' | 'role'>) => (
	<Icon icon={roleIcon[role.name]} {...props} />
)
const roleIcon = {
	ADMIN: faUserGear,
	LIMITED_ADMIN: faUserGear,
	USER: faUser,
	SUPER_ADMIN: faUserKey,
	INTERNAL_READ_ONLY: faUserLock,
} as const satisfies Record<UserRoleEntity['name'], IconDefinition>

export const RoleChip = ({ role, ...props }: { role: UserRoleEntity } & Omit<ComponentProps<typeof Chip>, 'color' | 'label' | 'role'>) => (
	<Chip color={colorByRole[role.name]} label={<I18n i18nKey={($) => $.users.roles[role.name]} />} {...props} />
)
const colorByRole = {
	ADMIN: 'info',
	LIMITED_ADMIN: 'info',
	USER: 'default',
	SUPER_ADMIN: 'success',
	INTERNAL_READ_ONLY: 'success',
} as const satisfies Record<UserRoleEntity['name'], ComponentProps<typeof Chip>['color']>
